import type { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { canHold, getReservationsByTask, sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { getLogger, LoggerName } from "~/modules/logger";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";





export interface HoldParticipantOptions {
    


    holdMusicUrl?: string;
    


    holdMusicMethod?: string;
}





















export class HoldParticipant implements Action<Promise<void>> {
    


    #targetParticipantSid: string;

    


    #taskSid: string;

    


    #options?: HoldParticipantOptions;

    constructor(targetParticipantSid: string, taskSid: string, options?: HoldParticipantOptions) {
        this.#targetParticipantSid = targetParticipantSid;
        this.#taskSid = taskSid;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Client);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const worker = taskRouter.worker;
        const holdMusicUrl = this.#options?.holdMusicUrl;
        const holdMusicMethod = this.#options?.holdMusicMethod || "GET";

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");
        if (!reservation || !canHold(reservation, worker)) {
            const errorMsg = "holdParticipant: Can't hold participant";
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation?.sid,
                targetParticipantSid: this.#targetParticipantSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const task = reservation.task;

        const participants = await cbm.getParticipantsByTask(task);
        const participant = participants.find((p) => p.participantSid === this.#targetParticipantSid);

        if (participant && participant.type === "external") {
            try {
                await cbm.holdParticipant(task, this.#targetParticipantSid);

                sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: reservation?.sid,
                    targetParticipantSid: this.#targetParticipantSid,
                    participantType: participant.type
                });

                return Promise.resolve();
            } catch (error) {
                const errorMsg = `holdParticipant: Could not hold external participant ${this.#targetParticipantSid}`;
                sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    reservationSid: reservation?.sid,
                    targetParticipantSid: this.#targetParticipantSid,
                    participantType: participant.type
                });
                logger.error(errorMsg, error);
                throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
            }
        }

        const workerParticipant = participants.find(
            (p) => p.routingProperties?.workerSid === this.#targetParticipantSid
        );
        if (workerParticipant && workerParticipant.routingProperties?.workerSid && workerParticipant.type === "agent") {
            try {
                await task.hold(workerParticipant.routingProperties?.workerSid, true, {
                    ...(holdMusicUrl && { holdUrl: holdMusicUrl }),
                    ...(holdMusicMethod && { holdMethod: holdMusicMethod })
                });

                sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid,
                    targetParticipantSid: this.#targetParticipantSid,
                    participantSid: workerParticipant.participantSid,
                    participantType: workerParticipant.type
                });

                return Promise.resolve();
            } catch (error) {
                const errorMsg = `holdParticipant: Could not hold worker participant ${this.#targetParticipantSid}`;
                sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid,
                    targetParticipantSid: this.#targetParticipantSid,
                    participantSid: workerParticipant.participantSid,
                    participantType: workerParticipant.type
                });
                logger.error(errorMsg, error);
                throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
            }
        }

        try {
            await task.updateParticipant({
                hold: true,
                ...(holdMusicUrl && { holdUrl: holdMusicUrl }),
                holdMethod: holdMusicMethod
            });

            sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantType: participant?.type,
                participantSid: participant?.participantSid
            });

            return Promise.resolve();
        } catch (error) {
            const errorMsg = `holdParticipant: Could not hold participant ${this.#targetParticipantSid}`;
            sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantType: participant?.type
            });
            logger.error(errorMsg, error);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }
    }
}
